import React, { useEffect, useReducer } from 'react';
import { hot } from 'react-hot-loader';
import Footer from 'common/Footer';
import StickyFooterLayout from 'common/StickyFooterLayout';
import StyledButton from 'common/StyledButton';
import Toast from 'common/Toast';
import useStarfishAPI from 'hooks/useStarfishAPI';
import { isEmptyString, isValidOrBlankEmail, isValidOrBlankPhoneNumber, isValidOrBlankUrl } from 'utils/text';
import TenantAdminForm from './TenantAdminForm';
import { initialState, reducer } from './reducer';

/**
 * @returns {StickyFooterLayout} the Help Desk Information configuration screen
 */
function App() {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [getResponse, callGetAPI] = useStarfishAPI();
    const [saveResponse, callSaveAPI] = useStarfishAPI();

    useEffect(() => {
        callGetAPI('../help-desk-information.html', { method: 'GET' });
    }, []);
    
    useEffect(() => {
        if (getResponse.success) dispatch({ type: 'LOAD_HELP_DESK_INFO', elements: getResponse.data });
    }, [getResponse]);

    const onSave = async () => {
        await callSaveAPI('../help-desk-information.html', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json; charset=UTF-8' },
            body: JSON.stringify({
                email: state.email,
                phone: state.phone,
                url: state.url,
                showToStaff: state.showToStaff,
                showToStudents: state.showToStudents,
                headerText: state.headerText,
                productHelpName: state.productHelpName,
                sectionTitle: state.sectionTitle,
                studentDisplayText: state.studentDisplayText,
                supportWebsiteDisplayText: state.supportWebsiteDisplayText
            })
        });
    }
    
    useEffect(() => {
        if (saveResponse.success) dispatch({ type: 'CHANGES_SAVED' });
    }, [saveResponse]);

    const clearChanges = () => {
        dispatch({ type: 'RESET_CHANGES' });
    }
    
    const disableSave = () =>
        (isEmptyString(state.email) && isEmptyString(state.phone) && isEmptyString(state.url))
        || !isValidOrBlankEmail(state.email) || !isValidOrBlankPhoneNumber(state.phone) || !isValidOrBlankUrl(state.url);
    
    const getToast = () => {
        if (!state.showToast) return null;
        return (
            <Toast message={state.toastText}
                   onTimeout={() => dispatch({ type: 'HIDE_TOAST' })} />
        );
    };
    
    const getContent = () => (
        <>
            <TenantAdminForm email={state.email}
                             phone={state.phone}
                             url={state.url}
                             showToStaff={state.showToStaff}
                             showToStudents={state.showToStudents}
                             headerText={state.headerText}
                             productHelpName={state.productHelpName}
                             sectionTitle={state.sectionTitle}
                             studentDisplayText={state.studentDisplayText}
                             supportWebsiteDisplayText={state.supportWebsiteDisplayText}
                             setValue={(values) => dispatch({type: 'SET_VALUE', elements: values})} />
            {getToast()}
        </>
    );
    
    const getFooter = () => (
        <Footer primaryButton={<StyledButton onClick={onSave} disabled={disableSave()}>Save</StyledButton>}
                secondaryButton={<StyledButton asLink onClick={clearChanges}>Cancel</StyledButton>} />
    );
    
    return <StickyFooterLayout content={getContent()}
                               footer={getFooter()} />
}

export default hot(module)(App);
